import { AxiosError, AxiosResponse, AxiosPromise } from 'axios';

import {
  ConsoleApiClient,
  EndpointParameters,
  EndpointReturnType,
} from '@cast/api-client';
import { makeLoader } from '@cast/fixtures/io';
import { isSimulatedDemoClusterId } from '@cast/utils';

import {
  getIsClusterDemoMode,
  getIsOpDemoMode,
} from '@cast/infrastructure-demo';

import { useRecorderState } from './recorder/recorder-state';

// Create the op-demo fixture loader
export const opDemoLoader = makeLoader('op-demo');

/** Request targets classic or OP demo fixture cluster. */
export const isSimulatedDemoClusterRequest = (args: unknown): boolean => {
  const requestParams = Array.isArray(args) ? args[0] : args;
  if (!requestParams || typeof requestParams !== 'object') return false;

  return Object.values(requestParams as Record<string, unknown>).some(
    (v) => typeof v === 'string' && isSimulatedDemoClusterId(v)
  );
};

export const rejectDemoClusterRequest = (): Promise<never> =>
  Promise.reject(
    new AxiosError(
      `Demo organization doesn't support this action`,
      '400',
      undefined,
      undefined,
      {
        data: { message: `Demo organization doesn't support this action` },
      } as AxiosResponse
    )
  );

export const rejectWithErrorMessage = (
  message: string
): Promise<AxiosResponse> =>
  Promise.reject(
    new AxiosError(message, '500', undefined, undefined, {
      data: {
        message,
      },
    } as AxiosResponse)
  );

export const getOrgId = (): string =>
  new URLSearchParams(window.location.search).get('org')!;

// OP Demo Mode helpers
export const getTourStepIdFromUrl = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('tourStepId');
};

// Recorder interceptor utility
type InterceptorConfig<
  FEATURE extends keyof ConsoleApiClient = any,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE] = any
> = {
  feature: FEATURE;
  endpoint: ENDPOINT;
  next: () => Promise<AxiosResponse<EndpointReturnType<FEATURE, ENDPOINT>>>;
  resolve: (
    data: EndpointReturnType<FEATURE, ENDPOINT>
  ) => AxiosPromise<EndpointReturnType<FEATURE, ENDPOINT>>;
  /** Original endpoint args for demoClusterFallback */
  args?: EndpointParameters<FEATURE, ENDPOINT>;
  /** Fallback data for getIsOpDemoMode() when no tour step fixture exists */
  opDemoFallback?: EndpointReturnType<FEATURE, ENDPOINT>;
  /** Loader for standard demo mode fixtures */
  demoFixtureLoader?: () => Promise<EndpointReturnType<FEATURE, ENDPOINT>>;
  /** Loader for demo cluster fallback (depends on args) */
  demoClusterFallback?: (
    args: EndpointParameters<FEATURE, ENDPOINT>
  ) =>
    | EndpointReturnType<FEATURE, ENDPOINT>
    | Promise<EndpointReturnType<FEATURE, ENDPOINT>>;
};

/**
 * TODO (Justas): delete after `https://castai.atlassian.net/browse/UI-5683` and `https://castai.atlassian.net/browse/UI-5682`
 * Utility to handle op-demo tour step interceptor logic with recording support.
 *
 * When recorder is active:
 * - Skips demo mode logic
 * - Calls actual endpoint
 * - Records the response
 *
 * When recorder is not active:
 * - Checks for tourStepId in URL
 * - Loads fixture if available
 * - Falls back to opDemoFallback if getIsOpDemoMode() is true
 * - Falls back to demoClusterFallback if getIsClusterDemoMode() is true
 * - Falls back to demoFixtureLoader if getIsClusterDemoMode() is true
 */
export const handleOpDemoInterceptor = async <
  FEATURE extends keyof ConsoleApiClient = any,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE] = any
>({
  feature,
  endpoint,
  next,
  resolve,
  args,
  opDemoFallback,
  demoFixtureLoader,
  demoClusterFallback,
}: InterceptorConfig<FEATURE, ENDPOINT>): AxiosPromise<
  EndpointReturnType<FEATURE, ENDPOINT>
> => {
  const recorderState = useRecorderState.getState();

  // When recorder is active: call actual endpoint, record response
  if (recorderState.isActive) {
    const response = await next();
    recorderState.recordData({
      feature,
      endpoint: endpoint as string,
      record: { data: response.data, parameters: args },
    });
    return response;
  }

  // Tour step mode: load fixture
  const tourStepId = getTourStepIdFromUrl();

  if (tourStepId) {
    const fixture = await opDemoLoader.loadFixture({
      feature,
      endpoint,
      state: tourStepId,
      endpointParameters: args,
    });
    if (fixture) {
      return resolve(fixture);
    }
  }

  // OP demo mode fallback (when no fixture exists for tour step)
  if (getIsOpDemoMode() && opDemoFallback !== undefined) {
    return resolve(opDemoFallback);
  }

  // Demo cluster fallback (args-dependent): classic demo or OP demo synthetic cluster
  if (
    demoClusterFallback &&
    args &&
    (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args))
  ) {
    const fixture = await demoClusterFallback(args);
    return resolve(fixture);
  }

  // Standard demo mode fallback
  if (getIsClusterDemoMode() && demoFixtureLoader) {
    const fixture = await demoFixtureLoader();
    return resolve(fixture);
  }

  return next();
};
